import { format } from 'date-fns';
import { deleteFirmware, OK, Result } from '../../api/api';
import { IFirmware, IFirmwareStatistic } from '../../api/types';
import { GridSection } from '../Common/Section';
import { UpdateProgress } from './UpdateProgress';
import { Button } from '../Common/Button';
import { ConfirmationModal } from '../Common/ConfirmationModal';
import { canDeleteFirmware } from '../../api/rules';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { StoredFirmwareInspection } from './StoredFirmwareInspection';

interface IParams {
  firmware: OK<IFirmware>;
  firmwareStatistic: OK<IFirmwareStatistic>;
  devicesOnProduct: number;
}

export const FirmwareInformation = ({
  firmware,
  firmwareStatistic,
  devicesOnProduct,
}: IParams) => {
  const navigate = useNavigate();
  const [deleteFeedback, setDeleteFeedback] = useState<string>('');
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  return (
    <GridSection>
      <div className="col-span-6">
        <div className="text-3xl pb-2">Information</div>
        <div className="font-thin">
          <div>
            <span>Firmware ID:</span>
            <span> {firmware.data.firmwareId}</span>
          </div>
          <div>
            <span>Semantic version:</span>
            <span> {firmware.data.firmwareVersion}</span>
          </div>
          <div>
            <span>Released: </span>
            <span>
              {format(
                new Date(firmware.data.releaseDate),
                'd-MM-yyyy',
              )}
            </span>
          </div>
          <div>
            <span>Is current firmware: </span>
            <span>{firmware.data.isCurrent ? 'true' : 'false'}</span>
          </div>
          <div>
            <span>Size: </span>
            <span>{formatBytes(firmware.data.size)}</span>
          </div>
        </div>
      </div>

      <div className="text-xl mt-3 col-span-6 flex flex-col items-end">
        <Button
          onClick={() => {
            setDeleteFeedback('');
            setIsDeleteConfirmationOpen(true);
          }}
          text={'Delete firmware'}
          disabled={[
            {
              disabled: !canDeleteFirmware(
                firmware.data,
                firmwareStatistic.data,
              ),
              reason: 'This firmware can not be deleted',
            },
            {
              disabled: isDeleting,
              reason: 'Firmware deletion is being submitted.',
            },
          ]}
        />
      </div>

      <div className="col-span-12">
        <StoredFirmwareInspection
          productCode={firmware.data.productCode.toString()}
          variant={firmware.data.variant}
          firmwareVersion={firmware.data.firmwareId.toString()}
        />
      </div>

      <UpdateProgress
        updatedCount={firmwareStatistic.data.reportedCount}
        desiredCount={firmwareStatistic.data.desiredCount}
        totalCount={devicesOnProduct}
      />
      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        title="Delete firmware"
        message={`Are you sure you want to permanently delete firmware ${firmware.data.firmwareId}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        pending={isDeleting}
        pendingText="Deleting..."
        errorMessage={deleteFeedback}
        onConfirm={handleDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteFeedback('');
            setIsDeleteConfirmationOpen(false);
          }
        }}
      />
    </GridSection>
  );

  function handleDelete() {
    if (!canDeleteFirmware(firmware.data, firmwareStatistic.data)) {
      setDeleteFeedback('Action not allowed!');
      return;
    }

    if (isDeleting) return;

    setDeleteFeedback('');
    setIsDeleting(true);
    deleteFirmware(
      firmware.data.productCode,
      firmware.data.variant,
      firmware.data.firmwareId.toString(),
    )
      .then((response: Result<string>) => {
        switch (response.status) {
          case 'OK':
            setIsDeleteConfirmationOpen(false);
            navigate(
              `/Products/${firmware.data.productCode}/Variant/${firmware.data.variant}`,
            );
            break;
          case 'Forbidden':
            setDeleteFeedback(
              "You don't have permission to do this.",
            );
            break;
          case 'Error':
          case 'Not Found':
          case 'Network issue':
            setDeleteFeedback(
              response.status +
                ': ' +
                response.errorMessages.join(', '),
            );
            break;
          default:
            setDeleteFeedback('Something went wrong...');
            break;
        }
      })
      .finally(() => {
        setIsDeleting(false);
      });
  }
};

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = [
    'Bytes',
    'KB',
    'MB',
    'GB',
    'TB',
    'PB',
    'EB',
    'ZB',
    'YB',
  ];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  );
}
